/**
 * Shared participant helpers used across the three Meetings pages
 * (workspaces list, single workspace meetings list, single meeting details).
 *
 * Co-locating ordering here guarantees an avatar's position means the same
 * thing on every page, so the user can rely on "my avatar is first, the rest
 * are alphabetical" no matter where they're looking.
 */

export type AvatarGroupEmployee = {
	_id: string;
	name: string;
};

export type ParticipantLike = {
	userId: string;
	name?: string;
};

type ToAvatarGroupEmployeesOptions = {
	/**
	 * The authenticated user's id. When provided and the user is in
	 * `participants`, they are placed first.
	 */
	currentUserId?: string;
	/**
	 * Optional name resolver for participants with missing/blank names (e.g.,
	 * removed users whose names live in a fallback lookup).
	 */
	resolveName?: (userId: string) => string | undefined;
};

/**
 * Build an ordered employee list for the py-components `<AvatarGroup />`.
 *
 * Order:
 *   1. The current user (if they are a participant)
 *   2. Everyone else, sorted alphabetically by display name (case-insensitive)
 *
 * Unnamed participants sort to the end so the avatar group doesn't lead with
 * blank-name avatars. Order is stable: ties fall back to insertion order.
 */
export const toAvatarGroupEmployees = (
	participants: ParticipantLike[] | undefined,
	{ currentUserId, resolveName }: ToAvatarGroupEmployeesOptions = {}
): AvatarGroupEmployee[] => {
	if (!participants || participants.length === 0) return [];

	const mapped: AvatarGroupEmployee[] = participants.map((p) => ({
		_id: p.userId,
		name: p.name || resolveName?.(p.userId) || "",
	}));

	const current = currentUserId ? mapped.find((p) => p._id === currentUserId) : undefined;
	const rest = mapped
		.filter((p) => p._id !== current?._id)
		.sort((a, b) => {
			// Empty-name avatars sort last so they don't lead the group.
			if (!a.name && b.name) return 1;
			if (a.name && !b.name) return -1;
			return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
		});

	return current ? [current, ...rest] : rest;
};
